import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { BrandApplication } from '@nucleus-commerce/api-client/platform';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useRejectApplication } from '../hooks/use-reject-application';
import { rejectSchema, type RejectValues } from '../schemas/reject-schema';

export interface RejectApplicationDialogProps {
  application: BrandApplication;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Rejects with a reason kept on the application; the switch also emails it to the applicant. */
export function RejectApplicationDialog({
  application,
  open,
  onOpenChange,
}: RejectApplicationDialogProps) {
  const { t } = useTranslation('applications');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('reject.title', { brand: application.brandName })}</DialogTitle>
          <DialogDescription>{t('reject.description')}</DialogDescription>
        </DialogHeader>
        {open ? <RejectForm application={application} onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

interface RejectFormProps {
  application: BrandApplication;
  onDone: () => void;
}

function RejectForm({ application, onDone }: RejectFormProps) {
  const { t } = useTranslation(['applications', 'common']);
  const reject = useRejectApplication(application.id);
  const form = useForm<RejectValues>({
    resolver: useI18nResolver(rejectSchema),
    defaultValues: { reason: '', notify: true },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await reject.mutateAsync(values);
      onDone();
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('applications:reject.reason')}</FormLabel>
              <FormControl>
                <Textarea
                  autoFocus
                  rows={4}
                  placeholder={t('applications:reject.reasonPlaceholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="notify"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start justify-between gap-4 rounded-lg border p-3">
              <div className="flex flex-col gap-1">
                <FormLabel>{t('applications:reject.notify')}</FormLabel>
                <FormDescription>
                  {t('applications:reject.notifyHint', { email: application.ownerEmail })}
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" variant="destructive" disabled={reject.isPending}>
            {reject.isPending
              ? t('applications:reject.confirming')
              : t('applications:reject.confirm')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
